import { MENU } from "./settings"
import * as React from "react"
import { useShell } from "./context"
import { Sections } from "../work/sections"

// Work page chrome: the client/section indicator (left) and the category tab
// (bottom). Ported from the Framer WorkNavigation component; it stays mounted
// from one work page to the next, so only its colours change between them.
const ASSETS = {
    dots: "https://framerusercontent.com/images/uRVL65qJKyCZ8kl0HxZIWiIB1ms.svg",
}
type Category = { label: string; href?: string }
interface Props {
    theme?: "dark" | "light"
    style?: React.CSSProperties
    categories: Category[]
    currentCategory: string
    homeUrl?: string
    workUrl: string
    feedUrl?: string
    contactUrl?: string
    previewSections: number
    clients: number
    barStep: number
    blur: number
    menuSpeed?: number
    submenuFont?: React.CSSProperties
    menuFont?: React.CSSProperties
    previewCategory?: boolean
    mobile?: boolean
}
const defaultCategories: Category[] = MENU.categories

export function WorkChrome({
    style,
    categories = defaultCategories,
    currentCategory = "Sports",
    homeUrl = "/",
    workUrl = "/sports",
    feedUrl = "/feed",
    contactUrl = "/about",
    previewSections = 6,
    clients = 4,
    barStep = 16,
    blur = 30,
    menuSpeed = 2,
    menuFont,
    submenuFont,
    previewCategory = false,
    mobile = false,
    theme = "dark",
    onNavigate,
}: Props & { onNavigate: (href: string) => boolean }) {
    const canvas = false
    const root = React.useRef<HTMLDivElement>(null)
    const menuButton = React.useRef<HTMLButtonElement>(null)
    const categoryWrap = React.useRef<HTMLDivElement>(null)
    const categoryButton = React.useRef<HTMLButtonElement>(null)
    const categoryItems = React.useRef<
        Array<HTMLAnchorElement | HTMLButtonElement | null>
    >([])
    const [groups, setGroups] = React.useState<number[]>([previewSections])
    const [current, setCurrent] = React.useState(0)
    const clientSections = React.useRef<HTMLElement[][]>([])
    const fills = React.useRef<Array<HTMLElement | null>>([])
    const progress = React.useRef(0)
    const total = Math.max(clients, groups.length)
    // Another page with more cases: the new dots open in; with fewer, the extra
    // ones close up before they go (same .6s as the lines).
    const [shown, setShown] = React.useState(total)
    // dots new in this render open in (decided while rendering, so they never
    // show at full size first)
    const prevTotal = React.useRef(total)
    const born = React.useRef(new Set<number>())
    if (total > prevTotal.current) for (let i = prevTotal.current; i < total; i++) born.current.add(i)
    prevTotal.current = total
    React.useEffect(() => {
        if (total >= shown) {
            setShown(total)
            const t = window.setTimeout(() => born.current.clear(), 700)
            return () => window.clearTimeout(t)
        }
        const t = window.setTimeout(() => setShown(total), 650)
        return () => window.clearTimeout(t)
    }, [total])
    const [categoryOpen, setCategoryOpen] = React.useState(false)
    // Theme changes (category to category) fade over 0.6s.
    const [shownTheme, setShownTheme] = React.useState(theme)
    const [carrying, setCarrying] = React.useState(false)
    React.useEffect(() => {
        if (theme === shownTheme) return
        setCarrying(true)
        setShownTheme(theme)
        const t = window.setTimeout(() => setCarrying(false), 1000)
        return () => window.clearTimeout(t)
    }, [theme])
    const [hydrated, setHydrated] = React.useState(false)
    React.useEffect(() => setHydrated(true), [])
    const categoryId = "work-categories-" + React.useId().replace(/:/g, "")
    const openCategories = canvas ? previewCategory : categoryOpen
    // the main menu lives in the shell: the two never stay open together
    const shell = useShell()
    const openMenu = (open: boolean) => shell?.setMenuOpen(open)
    React.useEffect(() => {
        if (shell?.menuOpen) setCategoryOpen(false)
    }, [shell?.menuOpen])
    React.useEffect(() => {
        const win = root.current?.ownerDocument.defaultView
        if (canvas) {
            setGroups([Math.max(1, previewSections)])
            setCurrent(0)
            progress.current = 0.35
            return
        }
        if (!win) return
        let raf = 0,
            last = -1
        const top = (el: HTMLElement) =>
            el.getBoundingClientRect().top + win.scrollY
        // Sections come from the controller registry, in page order. A section with
        // the "withClientStart" override begins a new client; until one exists,
        // every section belongs to the first client.
        const sync = () => {
            // (the next category at the end of the page has no dot)
            // and only the category on screen when the page holds several
            const els = Sections.get().filter(
                (el) =>
                    el.isConnected &&
                    el.getClientRects().length > 0 &&
                    !el.hasAttribute("data-nto-next") &&
                    (!Sections.current || el.dataset.feedPage === Sections.current)
            )
            els.sort((a, b) => top(a) - top(b))
            const g: HTMLElement[][] = []
            for (const el of els) {
                if (!g.length || el.hasAttribute("data-nto-client-start"))
                    g.push([])
                g[g.length - 1].push(el)
            }
            clientSections.current = g
            setGroups(g.map((s) => s.length))
            last = -1
            update()
        }
        // Active client = the one whose first section has reached mid-screen.
        // Bar fill = scroll progress from its first section to its last.
        const update = () => {
            const g = clientSections.current
            const y = win.scrollY,
                vh = win.innerHeight
            let a = 0
            for (let i = 0; i < g.length; i++)
                if (top(g[i][0]) <= y + vh * 0.5) a = i
            let p = 0
            const s = g[a]
            if (s) {
                const start = top(s[0])
                const end =
                    s[s.length - 1].getBoundingClientRect().bottom + y - vh
                p =
                    end > start
                        ? Math.max(0, Math.min(1, (y - start) / (end - start)))
                        : 1
            }
            progress.current = p
            // cases before the one on screen are done (full), the ones after not
            // started: a line that closes as the next one opens stays full
            fills.current.forEach((f, i) => {
                if (f) f.style.transform = `scaleY(${i === a ? p : i < a ? 1 : 0})`
            })
            if (a !== last) {
                last = a
                setCurrent(a)
            }
        }
        const scroll = () => {
            if (!raf)
                raf = win.requestAnimationFrame(() => {
                    raf = 0
                    update()
                })
        }
        const resize = () => {
            if (!raf)
                raf = win.requestAnimationFrame(() => {
                    raf = 0
                    sync()
                })
        }
        const key = (e: KeyboardEvent) => {
            if (e.key === "Escape") setCategoryOpen(false)
        }
        const unsubscribe = Sections.subscribe(sync)
        win.addEventListener("scroll", scroll, { passive: true })
        win.addEventListener("resize", resize)
        win.addEventListener("keydown", key)
        sync()
        return () => {
            win.cancelAnimationFrame(raf)
            unsubscribe()
            win.removeEventListener("scroll", scroll)
            win.removeEventListener("resize", resize)
            win.removeEventListener("keydown", key)
        }
    }, [canvas, previewSections])
    React.useEffect(() => {
        if (!categoryOpen) return
        const doc = root.current?.ownerDocument
        const outside = (event: PointerEvent) => {
            if (!categoryWrap.current?.contains(event.target as Node))
                setCategoryOpen(false)
        }
        doc?.addEventListener("pointerdown", outside)
        return () => doc?.removeEventListener("pointerdown", outside)
    }, [categoryOpen])
    const go = (el?: HTMLElement) => {
        const win = root.current?.ownerDocument.defaultView as any
        if (!win || canvas || !el) return
        // Same animated scroll as the cursor click, when the controller is on the page.
        if (Sections.goTo?.(el)) return
        win.scrollTo({
            top: el.getBoundingClientRect().top + win.scrollY,
            behavior: win.matchMedia("(prefers-reduced-motion: reduce)").matches
                ? ("instant" as ScrollBehavior)
                : "smooth",
        })
    }
    const goBar = (client: number, e: React.MouseEvent<HTMLElement>) => {
        const s = clientSections.current[client]
        if (!s || !s.length) return
        const r = e.currentTarget.getBoundingClientRect()
        const f = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height))
        go(s[Math.round(f * (s.length - 1))])
    }
    const categoryKeys = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            e.preventDefault()
            setCategoryOpen(false)
            categoryButton.current?.focus()
            return
        }
        if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return
        e.preventDefault()
        const items = categoryItems.current.filter(Boolean) as HTMLElement[]
        const current = items.indexOf(
            e.currentTarget.ownerDocument.activeElement as HTMLElement
        )
        const next =
            e.key === "Home"
                ? 0
                : e.key === "End"
                  ? items.length - 1
                  : (current +
                        (e.key === "ArrowDown" ? 1 : -1) +
                        items.length) %
                    items.length
        items[next]?.focus()
    }
    const stop = (e: React.SyntheticEvent) => e.stopPropagation()
    return (
        <div
            ref={root}
            className="nto27 nto-work-navigation"
            data-theme={shownTheme}
            data-carry-fade={carrying ? "true" : undefined}
            data-hydrated={hydrated ? "true" : undefined}
            data-live="true"
            data-mobile={mobile ? "true" : "false"}
            style={{
                ...style,
                position: "relative",
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                color: "white",
                fontFamily: "NTO27Blubird, sans-serif",
                fontWeight: 600,
                ["--nto-blur" as any]: `${blur / 2}px`,
            }}
        >
            {total > 0 && (
                <nav
                    className="work-sections"
                    aria-label="Clients"
                    onClick={stop}
                    onWheel={stop}
                >
                    {Array.from({ length: Math.max(shown, total) }, (_, i) => {
                        const n = groups[i] || 0
                        const leaving = i >= total
                        const on = current === i && n > 0 && !leaving
                        const bar = Math.max(1, n) * barStep
                        return (
                            <button
                                key={i}
                                className="work-section"
                                aria-label={
                                    n
                                        ? `Go to client ${i + 1} of ${total}`
                                        : `Client ${i + 1} coming soon`
                                }
                                aria-current={on ? "true" : undefined}
                                aria-disabled={n ? undefined : "true"}
                                aria-hidden={leaving || undefined}
                                data-leave={leaving || undefined}
                                data-enter={born.current.has(i) || undefined}
                                style={{ height: leaving ? 0 : on ? bar + 37 : 37 }}
                                onClick={(e) =>
                                    on
                                        ? goBar(i, e)
                                        : go(clientSections.current[i]?.[0])
                                }
                            >
                                <span className="work-dot" />
                                <span
                                    className="work-bar"
                                    style={{ height: bar }}
                                >
                                    <i
                                        className="work-fill"
                                        ref={(f) => {
                                            fills.current[i] = f
                                        }}
                                        style={{
                                            transform: `scaleY(${on ? progress.current : i < current ? 1 : 0})`,
                                        }}
                                    />
                                </span>
                            </button>
                        )
                    })}
                </nav>
            )}
            <div
                ref={categoryWrap}
                className="work-category"
                data-open={openCategories}
                onClick={stop}
                onWheel={stop}
            >
                <button
                    ref={categoryButton}
                    className="work-category-toggle"
                    aria-expanded={openCategories}
                    aria-controls={categoryId}
                    aria-haspopup="menu"
                    onClick={() => {
                        setCategoryOpen((v) => !v)
                        openMenu(false)
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                            e.preventDefault()
                            setCategoryOpen(true)
                            requestAnimationFrame(() =>
                                categoryItems.current[0]?.focus()
                            )
                        }
                    }}
                >
                    <span className="tab-art" aria-hidden="true" />
                    <span className="category-label">{currentCategory}</span>
                    <img className="category-dots" src={ASSETS.dots} alt="" />
                </button>
                <div
                    id={categoryId}
                    role="menu"
                    aria-label="Work category"
                    className="category-list"
                    onKeyDown={categoryKeys}
                    style={{
                        visibility: openCategories ? "visible" : "hidden",
                        transform: openCategories
                            ? "translateY(0) scale(1)"
                            : "translateY(14px) scale(.97)",
                    }}
                >
                    <span className="category-panel-art" aria-hidden="true" />
                    {categories.map((item, i) => {
                        const category = {
                            ...item,
                            href:
                                item.href ||
                                (item.label.toLowerCase() ===
                                currentCategory.toLowerCase()
                                    ? workUrl
                                    : ""),
                        }
                        return category.href ? (
                            <a
                                ref={(el) => {
                                    categoryItems.current[i] = el
                                }}
                                key={i}
                                role="menuitem"
                                className="category-option"
                                aria-current={
                                    category.label.toLowerCase() ===
                                    currentCategory.toLowerCase()
                                        ? "true"
                                        : undefined
                                }
                                href={category.href}
                                tabIndex={openCategories ? 0 : -1}
                                onClick={(e) => {
                                    setCategoryOpen(false)
                                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                                    // (the category on screen, not the address)
                                    if (category.label.toLowerCase() === currentCategory.toLowerCase())
                                        return e.preventDefault()
                                    if (onNavigate(category.href)) e.preventDefault()
                                }}
                            >
                                {category.label}
                            </a>
                        ) : (
                            <button
                                ref={(el) => {
                                    categoryItems.current[i] = el
                                }}
                                key={i}
                                role="menuitem"
                                className="category-option"
                                aria-disabled="true"
                                title="Destination not connected yet"
                                tabIndex={openCategories ? 0 : -1}
                            >
                                {category.label}
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
